import {GoogleOAuthProvider} from "@react-oauth/google";
import ReactGA from "react-ga";
import * as Sentry from "@sentry/react";
import {Navigate, Route, Routes, useNavigate, useLocation, Link} from "react-router-dom";
import {PrivateRoutes} from "./components/private-routes";
import {history} from "./components/private-routes/history";
import {OnboardingDataProvider} from "./container/onboarding/onboarding-context";
import PageNotFound from "./pages/404";
import EmailVerificationPage from "./pages/auth/email-verification";
import ForgotPasswordPage from "./pages/auth/forgot-password";
import LoginPage from "./pages/auth/login";
import CreateNewPassword from "./pages/auth/new-password";
import RegisterPage from "./pages/auth/register";
import {EmailVerificationSuccessPage} from "./pages/auth/verify-success";
import OnboardingPage from "./pages/onboarding";
import TwoFactorNudgePage from "./pages/auth/two-factor-nudge";
import {IS_TWO_FACTOR_FEATURE_ENABLED} from "./services/config";
import {ErrorBoundary} from "react-error-boundary";
// import {GoogleOAuthProvider} from "@react-oauth/google";
import "./App.css";
import RootRedirect from "./pages/root-redirect";
import JoinMeetingPage from "./pages/meeting/join";
import CustomerCancelBookingPage from "./pages/booking/cancel-booking";
import CustomerReschedulePage from "./pages/booking/reschedule";
import CustomerLoginPage from "./pages/auth/login/customer";
import {CustomerCallsPage} from "./pages/dashboard/customer/calls";
import {CustomerEmailVerificationPage} from "./pages/auth/login/customer/verify-link";
import {Alert, Button, Result} from "antd";
import {MessageProvider} from "./context/message-context";
import {CustomerMessagesPage} from "./pages/dashboard/customer/messages";
import {StripeSuccessPage} from "./pages/stripe-success";
import {CustomerLiveClassesPage} from "./pages/dashboard/customer/live-classes";
import LiveClassJoinMeetingPage from "./pages/meeting/join/live-classes";
import {CustomerDashboardHomepage} from "./pages/dashboard/customer/home";
import {CustomerProductsPage} from "./pages/dashboard/customer/products";
import {CustomerProductDetailsPage} from "./pages/dashboard/customer/products/details";
import {DashboardRoutes} from "./utils/routes/routes";
import {GetProfileProvider} from "./context/profile/profile-context";
import {GetCoachConnectCalendarStatusProvider} from "./context/connect-calendar/connect-calendar-status.context";
import {
  NavigateToLiveClassRebrand,
  NavigateToNewLiveClass,
  NavigateToNewProducts,
  NavigateToNewServices,
  NavigateToNewServices2,
  NavigateToProductsRebrand,
  NavigateToPublicProfileRebrand,
  NavigateToServicesRebrand,
} from "./pages/public-profile/navigate";
import {StyledPageHeader} from "./styles/dashboard/calls/calls";
import {CustomerDashboardWrapper} from "./layouts/customer-dashboard-wrapper";
import {
  StyledProductDetailsMainSectionContainer,
  StyledProductDetailsPageContainer,
  StyledProductDetailsWrapper,
} from "./components/products/product-details/product-details.style";
import {ArrowLeftOutlined} from "@ant-design/icons";
import {AuthFormWrapper} from "./layouts/auth-form-wrapper";
import {StyledAuthTitleCenterAlign} from "./layouts/auth-form-wrapper/auth-form-wrapper.style";
import {GetCustomerUpcomingCallsProvider} from "./context/upcoming-calls/customer";
import {GetCustomerPastCallsProvider} from "./context/past-calls/customer";
import {GetKycStatusProvider} from "./kyc/useCases/get-kyc-status/context/get-kyc-status";
import {startCoachliTracker} from "./mixpanel";
import {captureSignupSource} from "./utils/signup-tracking";
import {lazy, Suspense, useEffect} from "react";
// import {StatusBar} from "./components/status-bar";

// console.log(process.env.REACT_APP_GOOGLE_ANALYTICS_TRACKING_ID as string);
// ReactGA.initialize(process.env.REACT_APP_GOOGLE_ANALYTICS_TRACKING_ID as string);
ReactGA.initialize(process.env.REACT_APP_GOOGLE_ANALYTICS_TRACKING_ID as string);

// Sentry-instrumented <Routes> so transactions are named by route pattern
// (e.g. "/:username/products/details") instead of raw URLs.
const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);
const PublicProfileVisualQaPage =
  process.env.NODE_ENV !== "production"
    ? lazy(() =>
        import("./public-profile-page/page/visual-qa").then((module) => ({
          default: module.PublicProfileVisualQaPage,
        }))
      )
    : null;

const App = () => {
  //init custom history object to allow navigation from
  // anywhere in the react app (inside or outside components)
  const navigate = useNavigate();
  history.navigate = useNavigate();
  history.location = useLocation();

  useEffect(() => {
    // Initialise and wait for it to finish before firing any queries
    startCoachliTracker("coachli-site-id").then(() => {
      // you can now safely fire queries that depend on the headers
    });
    captureSignupSource();
  }, []);

  return (
    <>
      {/* <StatusBar /> */}
      <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID as string}>
        <ErrorBoundary
          FallbackComponent={({error, resetErrorBoundary}) => (
            <div>
              <Result
                status="500"
                title="Sorry, something went wrong."
                subTitle={error.message}
                extra={
                  <Button
                    type="primary"
                    onClick={() => {
                      resetErrorBoundary();
                    }}
                    size="small"
                  >
                    Go back
                  </Button>
                }
              />
            </div>
          )}
          onError={(error, info) => {
            Sentry.captureException(error, {
              extra: {componentStack: info?.componentStack},
            });
            console.error({error, info});
          }}
          onReset={() => navigate(-1)}
        >
          <SentryRoutes>
            <Route path="*" element={<PageNotFound />} />
            <Route path="/" element={<RootRedirect />} />
            {PublicProfileVisualQaPage ? (
              <Route
                path="/__visual-qa/public-profile"
                element={
                  <Suspense fallback={null}>
                    <PublicProfileVisualQaPage />
                  </Suspense>
                }
              />
            ) : null}

            {/* AUTH PAGES */}
            <Route
              path="/auth/register"
              element={
                <ErrorBoundary
                  FallbackComponent={({error, resetErrorBoundary}) => (
                    <AuthFormWrapper>
                      <StyledAuthTitleCenterAlign>
                        <div>
                          <h2 className="form-main-heading">Create an account</h2>
                        </div>
                      </StyledAuthTitleCenterAlign>
                      <div>
                        <Alert
                          message="Sorry, something went wrong."
                          showIcon
                          description={error.message}
                          type="error"
                          action={
                            <Button
                              onClick={() => {
                                resetErrorBoundary();
                              }}
                              size="small"
                              danger
                            >
                              Reset
                            </Button>
                          }
                        />
                      </div>
                    </AuthFormWrapper>
                  )}
                >
                  <RegisterPage />
                </ErrorBoundary>
              }
            />
            <Route path="/auth/login" element={<LoginPage />} />
            <Route path="/auth/customer/login" element={<CustomerLoginPage />} />
            <Route path="/auth/email-verification" element={<EmailVerificationPage />} />
            <Route
              path="/auth/verify/:email/:link"
              element={<EmailVerificationSuccessPage />}
            />
            <Route
              path="/auth/customer/verify/:email/:link"
              element={<CustomerEmailVerificationPage />}
            />
            <Route path="/auth/forgot-password" element={<ForgotPasswordPage />} />
            <Route
              path="/auth/recover-account/:email/:link"
              element={<CreateNewPassword />}
            />

            <Route
              path="/:username/services/details"
              element={<NavigateToServicesRebrand />}
            />
            <Route
              path="/:username/services/booking/success"
              element={<NavigateToPublicProfileRebrand />}
            />
            <Route path="/services/details" element={<NavigateToNewServices />} />
            <Route
              path="/:username/live-classes/details"
              element={<NavigateToLiveClassRebrand />}
            />
            <Route
              path="/:username/live-classes/booking/success"
              element={<NavigateToPublicProfileRebrand />}
            />
            <Route path="/live-classes/details" element={<NavigateToNewLiveClass />} />
            <Route path="/booking/stripe-success" element={<StripeSuccessPage />} />
            <Route
              path="/:username/products/details"
              element={<NavigateToProductsRebrand />}
            />
            <Route
              path="/:username/products/booking/success"
              element={<NavigateToPublicProfileRebrand />}
            />
            <Route path="/products/details" element={<NavigateToNewProducts />} />
            <Route
              path="/:username/meetings/:slug/:id"
              element={<NavigateToNewServices2 />}
            />
            <Route path="/meetings/call/join/:reference" element={<JoinMeetingPage />} />
            <Route
              path="/meetings/call/live-class/join/:reference"
              element={<LiveClassJoinMeetingPage />}
            />
            <Route
              path="/booking/cancel/:reference"
              element={<CustomerCancelBookingPage />}
            />
            <Route
              path="/booking/reschedule/:reference"
              element={<CustomerReschedulePage />}
            />

            <Route element={<PrivateRoutes />}>
              <Route
                path="/onboarding"
                element={
                  <OnboardingDataProvider>
                    <OnboardingPage />
                  </OnboardingDataProvider>
                }
              />
              {/*
                Kept registered while 2FA is off, redirecting instead of 404ing:
                anyone who bookmarked the nudge, or was mid-flow when the feature
                was pulled, lands on the dashboard rather than a dead route.
              */}
              <Route
                path="/auth/2fa-setup"
                element={
                  IS_TWO_FACTOR_FEATURE_ENABLED ? (
                    <GetProfileProvider>
                      <TwoFactorNudgePage />
                    </GetProfileProvider>
                  ) : (
                    <Navigate to="/onboarding" replace />
                  )
                }
              />

              {/* COACH PAGES */}
              {DashboardRoutes.map((route, index) => (
                <Route
                  key={index}
                  path={route.path}
                  element={
                    <GetProfileProvider>
                      <GetCoachConnectCalendarStatusProvider>
                        <GetKycStatusProvider>{route.element}</GetKycStatusProvider>
                      </GetCoachConnectCalendarStatusProvider>
                    </GetProfileProvider>
                  }
                />
              ))}
              {/* CUSTOMER PAGES */}
              <Route path="/customer/home" element={<CustomerDashboardHomepage />} />
              <Route
                path="/customer/calls"
                element={
                  <ErrorBoundary
                    FallbackComponent={({error, resetErrorBoundary}) => (
                      <CustomerDashboardWrapper pageTitle={"1:1 Sessions"}>
                        <StyledPageHeader>1:1 Sessions</StyledPageHeader>
                        <div style={{marginTop: "1.8rem"}}>
                          <Alert
                            message="Sorry, something went wrong."
                            showIcon
                            description={error.message}
                            type="error"
                            action={
                              <Button
                                onClick={() => {
                                  resetErrorBoundary();
                                }}
                                size="small"
                                danger
                              >
                                Reset
                              </Button>
                            }
                          />
                        </div>
                      </CustomerDashboardWrapper>
                    )}
                  >
                    <GetCustomerUpcomingCallsProvider>
                      <GetCustomerPastCallsProvider>
                        <CustomerCallsPage />
                      </GetCustomerPastCallsProvider>
                    </GetCustomerUpcomingCallsProvider>
                  </ErrorBoundary>
                }
              />
              <Route
                path="/customer/products"
                element={
                  <ErrorBoundary
                    FallbackComponent={({error, resetErrorBoundary}) => (
                      <CustomerDashboardWrapper pageTitle={"Products"}>
                        <StyledPageHeader>Products</StyledPageHeader>
                        <div style={{marginTop: "1.5rem"}}>
                          <Alert
                            message="Sorry, something went wrong."
                            showIcon
                            description={error.message}
                            type="error"
                            action={
                              <Button
                                onClick={() => {
                                  resetErrorBoundary();
                                }}
                                size="small"
                                danger
                              >
                                Reset
                              </Button>
                            }
                          />
                        </div>
                      </CustomerDashboardWrapper>
                    )}
                  >
                    <CustomerProductsPage />
                  </ErrorBoundary>
                }
              />
              <Route
                path="/customer/products/details/:id"
                element={
                  <ErrorBoundary
                    FallbackComponent={({error, resetErrorBoundary}) => (
                      <div style={{position: "relative", minHeight: "100vh"}}>
                        <StyledProductDetailsWrapper>
                          <StyledProductDetailsPageContainer>
                            <Link to="/customer/products">
                              <Button
                                icon={<ArrowLeftOutlined />}
                                type="link"
                                size="small"
                              >
                                Back
                              </Button>
                            </Link>
                            <StyledProductDetailsMainSectionContainer>
                              <div>
                                <Alert
                                  message="Sorry, something went wrong."
                                  showIcon
                                  description={error.message}
                                  type="error"
                                  action={
                                    <Button
                                      onClick={() => {
                                        resetErrorBoundary();
                                      }}
                                      size="small"
                                      danger
                                    >
                                      Reset
                                    </Button>
                                  }
                                />
                              </div>
                            </StyledProductDetailsMainSectionContainer>
                          </StyledProductDetailsPageContainer>
                        </StyledProductDetailsWrapper>
                      </div>
                    )}
                  >
                    <CustomerProductDetailsPage />
                  </ErrorBoundary>
                }
              />
              <Route
                path="/customer/live-classes"
                element={
                  <ErrorBoundary
                    FallbackComponent={({error, resetErrorBoundary}) => (
                      <CustomerDashboardWrapper pageTitle={"Live events"}>
                        <StyledPageHeader>Live events</StyledPageHeader>
                        <div style={{marginTop: "1.5rem"}}>
                          <Alert
                            message="Sorry, something went wrong."
                            showIcon
                            description={error.message}
                            type="error"
                            action={
                              <Button
                                onClick={() => {
                                  resetErrorBoundary();
                                }}
                                size="small"
                                danger
                              >
                                Reset
                              </Button>
                            }
                          />
                        </div>
                      </CustomerDashboardWrapper>
                    )}
                  >
                    <CustomerLiveClassesPage />
                  </ErrorBoundary>
                }
              />
              <Route
                path="/customer/reschedule/:reference"
                element={<CustomerReschedulePage />}
              />
              <Route
                path="/customer/messages"
                element={
                  <ErrorBoundary
                    FallbackComponent={({error, resetErrorBoundary}) => (
                      <CustomerDashboardWrapper pageTitle={"Messages"}>
                        <StyledPageHeader>Messages</StyledPageHeader>
                        <div style={{marginTop: "1.5rem"}}>
                          <Alert
                            message="Sorry, something went wrong."
                            showIcon
                            description={error.message}
                            type="error"
                            action={
                              <Button
                                onClick={() => {
                                  resetErrorBoundary();
                                }}
                                size="small"
                                danger
                              >
                                Reset
                              </Button>
                            }
                          />
                        </div>
                      </CustomerDashboardWrapper>
                    )}
                  >
                    <MessageProvider>
                      <CustomerMessagesPage />
                    </MessageProvider>
                  </ErrorBoundary>
                }
              />
            </Route>
          </SentryRoutes>
        </ErrorBoundary>
      </GoogleOAuthProvider>
    </>
  );
};

export default App;
